import { MantineThemeOverride } from '@mantine/core';

const modalSizeWidths: Record<string, string> = {
  xs: '40vw',
  sm: '50vw',
  md: '65vw',
  lg: '75vw',
  xl: '85vw',
};

const modalSizeMinHeights: Record<string, string> = {
  md: '55vh',
  lg: '70vh',
  xl: '80vh',
};

function modalWidth(size: string | number | undefined): string {
  if (typeof size === 'number') {
    return `${size}px`;
  }

  if (typeof size === 'string') {
    if (modalSizeWidths[size]) {
      return modalSizeWidths[size];
    }

    if (size.includes('%') || size.includes('vw') || size.includes('px') || size === 'auto') {
      return size;
    }
  }

  return modalSizeWidths.xl;
}

function modalMinHeight(size: string | number | undefined): string | undefined {
  if (typeof size === 'string' && modalSizeMinHeights[size]) {
    return modalSizeMinHeights[size];
  }

  if (typeof size === 'number' || size === 'xs' || size === 'sm') {
    return undefined;
  }

  return modalSizeMinHeights.xl;
}

const MOBILE_MODAL_SAFE_HEIGHT =
  'calc(100dvh - 1.5rem - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))';

const theme: MantineThemeOverride = {
  primaryColor:'brand',
  primaryShade:6,
  colors:{brand:['#f2f8f4','#e2eee6','#c7ddcf','#abcbb8','#86b39a','#5c9476','#30715b','#285f4c','#204c3d','#173b2e']},
  components: {
    ScrollArea: {
      defaultProps: { scrollbarSize: 10 },
      styles: {
        scrollbar: { backgroundColor: '#edf3ef', '&:hover': { backgroundColor: '#edf3ef' } },
        thumb: { backgroundColor: '#8fa99a', borderRadius: 8, '&:hover': { backgroundColor: '#779584' } },
        corner: { backgroundColor: '#edf3ef' },
      },
    },
    Modal: {
      defaultProps: {
        size: 'xl',
        yOffset: '2dvh',
        xOffset: '2.5vw',
      },
      styles: (_theme, { size }) => {
        const minHeight = modalMinHeight(size);

        return {
          inner: {
            '@media (max-width: 768px)': {
              alignItems: 'flex-start',
              paddingTop: 'max(0.75rem, env(safe-area-inset-top, 0px))',
              paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))',
            },
          },
          content:
            size === 'full'
              ? {
                  '@media (max-width: 768px)': {
                    maxHeight: MOBILE_MODAL_SAFE_HEIGHT,
                  },
                }
              : {
                  flex: `0 0 ${modalWidth(size)}`,
                  maxWidth: '95vw',
                  maxHeight: '92dvh',
                  ...(minHeight ? { minHeight } : {}),
                  '@media (max-width: 768px)': {
                    flex: '0 1 auto',
                    width: 'calc(100vw - 1.5rem)',
                    maxWidth: 'calc(100vw - 1.5rem)',
                    minHeight: 'unset',
                    maxHeight: MOBILE_MODAL_SAFE_HEIGHT,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  },
                },
          header: {
            '@media (max-width: 768px)': {
              flexShrink: 0,
            },
          },
          body: {
            '@media (max-width: 768px)': {
              overflowY: 'auto',
              flex: 1,
              minHeight: 0,
            },
          },
        };
      },
    },
    Button: {
      styles: (currentTheme, { color, variant }) => {
        const semantic=Boolean(color && !['brand','green','teal'].includes(color));
        const palette=currentTheme.colors[color || 'brand'] || currentTheme.colors.brand;
        const primary=semantic?palette[6]:'#30715b';
        const filled=variant==='filled';
        return {root:{
          borderRadius:'8px',border:`1px solid ${semantic?palette[5]:filled?'#245944':'#95b5a2'}`,
          backgroundColor:semantic?undefined:filled?primary:variant==='default'?'#fff':'#edf5f0',
          color:semantic?undefined:filled?'#fff':'#245944',
          cursor:'pointer',transition:'background-color 0.2s,border-color 0.2s',
          '&:hover':{backgroundColor:semantic?undefined:filled?'#245944':'#dcece2',borderColor:semantic?palette[7]:'#30715b'},
          '&:focus-visible':{outline:'2px solid #7ca78c',outlineOffset:2},
          '&:disabled, &[data-disabled], &:disabled:hover, &[data-disabled]:hover':{backgroundColor:'#e9ecef',color:'#868e96',borderColor:'#ced4da',cursor:'not-allowed',opacity:1},
        }};
      },
    },
    TextInput: {
      styles: {
        input: {
          borderColor: '#ced4da',
          '&:focus': {
            borderColor: '#204C3D',
          },
        },
      },
    },
    PasswordInput: {
      styles: {
        input: {
          borderColor: '#ced4da',
          '&:focus-within': {
            borderColor: '#204C3D',
          },
        },
      },
    },
    Switch: {
      styles: {
        track: {
          backgroundColor: '#ced4da',
          borderColor: '#ced4da',
          transition: 'background-color 0.25s, border-color 0.25s',
          'input:checked + &': {
            backgroundColor: '#30715B',
            borderColor: '#30715B',
          },
        },
        thumb: {
          backgroundColor: '#fff',
        },
      },
    },
    ActionIcon: {
      defaultProps:{color:'brand'},
      styles:(currentTheme,{color,variant})=>{
        const semantic=Boolean(color&&!['brand','green','teal'].includes(color));
        const palette=currentTheme.colors[color||'brand'];
        const primary=semantic?(palette?.[6]||color):'#30715b';
        const border=semantic?(palette?.[3]||color):'#a9c4b5';
        return {root:{color:variant==='filled'?'#fff':primary,border:`1px solid ${border}`,
          backgroundColor:variant==='filled'?primary:semantic?undefined:'#f2f7f4',
          transition:'background-color 0.2s,border-color 0.2s',
          '&:hover':{backgroundColor:semantic?undefined:'#dcece2',borderColor:primary},
          '&:disabled, &[data-disabled], &:disabled:hover, &[data-disabled]:hover':{backgroundColor:'#e9ecef',color:'#868e96',borderColor:'#ced4da',cursor:'not-allowed',opacity:1},
        }};
      },
    },
    Tabs: {
      styles: {
        tab: {
          '&[data-active]': {
            borderColor: '#30715B',
            '&:hover': {
              borderColor: '#204C3D',
            },
          },
        },
      },
    },
    Select: {
      styles: {
        // O slot "input" é responsável pela caixa de seleção
        input: {
          borderColor: '#ced4da',
          '&:focus': {
            borderColor: '#204C3D',
          },
        },
        // O slot "item" é responsável pelos itens do dropdown
        item: {
          '&[data-selected]': {
            backgroundColor: '#30715B',
            '&:hover': {
              backgroundColor: '#204C3D',
            },
          },
        },
      },
    },
    Badge: {
      styles: {
        root: {
          backgroundColor: '#30715B',
          color: '#fff',
        },
      },
    },
    DateInput: {
      styles: {
        input: {
          borderColor: '#ced4da',
          '&:focus': {
            borderColor: '#204C3D',
          },
        },
        dropdown: {
          backgroundColor: '#fff',
          border: '1px solid #30715B',
          borderRadius: '8px',
          width: '220px',
          minWidth: '220px',
          maxWidth: '220px',
        },
        day: {
          '&[data-selected]': {
            backgroundColor: '#30715B',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#204C3D',
            },
          },
          '&[data-today]': {
            borderColor: '#30715B',
          },
          '&:hover': {
            backgroundColor: 'rgba(48,113,91,0.2)',
          },
        },
        weekday: {
          color: '#30715B',
        },
        month: {
          color: '#204C3D',
        },
      },
    },
    Textarea: {
      styles: {
        input: {
          borderColor: '#ced4da',
          '&:focus': {
            borderColor: '#204C3D',
          },
          '&:focus-within': {
            borderColor: '#204C3D',
          },
        },
      },
    },
    NumberInput: {
      styles: {
        input: {
          borderColor: '#ced4da',
          '&:focus': {
            borderColor: '#204C3D',
          },
        },
      },
    },
    Checkbox: {
      styles: {
        input: {
          borderColor: '#ced4da',
          backgroundColor: '#fff',
          transition: 'border-color 0.25s, background-color 0.25s',
          '&:hover': {
            borderColor: '#204C3D',
          },
          '&:checked': {
            backgroundColor: '#30715B',
            borderColor: '#30715B',
          },
          '&:checked:hover': {
            backgroundColor: '#204C3D',
            borderColor: '#204C3D',
          },
        },
        icon: {
          color: '#fff',
        },
        label: {
          color: '#000',
        },
      },
    },
    Autocomplete: {
      styles: {
        input: {
          borderColor: '#ced4da',
          '&:focus': {
            borderColor: '#204C3D',
          },
        },
        item: {
          '&[data-selected]': {
            backgroundColor: '#30715B',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#204C3D',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(48,113,91,0.2)',
          },
        },
      },
    },
  },
};

export default theme;
